import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ViewService } from '../../../services/common/view.service';
import { DataService } from '../../../services/common/data.service';

/**
 * Modal secondario aperto dal popup avviso quando la CTA è di tipo
 * `custom_form` o `ticket`. Contiene i campi del form e gestisce l'invio.
 *
 * Flow:
 *  - parent (AlertPopupPage) apre questo modal al click del bottone CTA
 *  - utente compila i campi e preme "Invia"
 *  - modal si chiude restituendo { submitted: true, data: formData }
 *    oppure { submitted: false } se l'utente annulla
 *  - parent esegue il tracking via API
 */
@Component({
  selector: 'app-alert-form-modal',
  templateUrl: './alert-form-modal.page.html',
  styleUrls: ['./alert-form-modal.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlertFormModalPage implements OnInit {

  @Input() alert: any;
  @Input() view: ViewService;
  @Input() ctaLabel: string = 'Invia';
  /** Titolo da mostrare nell'header del form modal (già risolto nella lingua utente).
   *  Tipicamente corrisponde al titolo dell'avviso padre. */
  @Input() title: string = '';
  /** Testo opzionale mostrato sopra ai campi del form (risolto già nella lingua utente). */
  @Input() formDescription: string = '';

  formData: any = {};
  closeAction: any;
  submitting: boolean = false;

  constructor(
    private cd: ChangeDetectorRef,
    private modalCtrl: ModalController,
    public data: DataService
  ) {}

  ngOnInit() {
    this.closeAction = () => { this.cancel(); };

    // Pre-popolo i campi del form custom
    if (this.alert?.cta_type === 'custom_form' && this.alert?.cta_config?.fields) {
      for (const field of this.alert.cta_config.fields) {
        this.formData[field.name] = '';
      }
    }
  }

  get isCustomForm(): boolean {
    return this.alert?.cta_type === 'custom_form';
  }

  get isTicket(): boolean {
    return this.alert?.cta_type === 'ticket';
  }

  /**
   * Valida e invia il form restituendo i dati al parent.
   */
  async submit() {
    if (this.submitting) return;

    // Validazione minima: per custom_form controllo i campi required
    if (this.isCustomForm) {
      const fields = this.alert?.cta_config?.fields || [];
      for (const field of fields) {
        if (field.required && !this.formData[field.name]) {
          const lbl = this.resolveFieldLabel(field);
          await this.view?.presentAlert?.('Campo mancante', `Il campo "${lbl}" è obbligatorio`, ['Ok']);
          return;
        }
      }
    }

    if (this.isTicket) {
      if (!this.formData['subject'] || !this.formData['body']) {
        await this.view?.presentAlert?.('Campi mancanti', 'Oggetto e messaggio sono obbligatori', ['Ok']);
        return;
      }
    }

    this.submitting = true;
    this.cd.markForCheck();

    await this.modalCtrl.dismiss({
      submitted: true,
      data: { ...this.formData }
    });
  }

  async cancel() {
    await this.modalCtrl.dismiss({ submitted: false });
  }

  resolveFieldLabel(field: any): string {
    if (!field?.label) return field?.name || '';
    if (typeof field.label === 'string') return field.label;
    const lang = this.data?.selOrganization?.translate_language || 'it';
    return field.label[lang] || field.label['it'] || field.name || '';
  }
}
